import { ref } from 'vue';
import { READING_ASSISTANCE_WS_URL } from '@/assets/constants/api_endpoints';
import {
    WS_REQUEST_SUMMARIZE,
    WS_REQUEST_GENERATE_QUESTIONS,
    WS_MESSAGE_SUMMARY_TOKEN,
    WS_MESSAGE_SUMMARY_DONE,
    WS_MESSAGE_QUESTION,
    WS_MESSAGE_QUESTIONS_DONE,
    WS_MESSAGE_ERROR,
} from '@/assets/constants/constants';

/**
 * Composable for the reading-assistance WebSocket.
 * Handles streaming summary tokens and question objects.
 */
export function useReadingAssistance() {
    const socket = ref(null);

    const initializeSocket = (authToken = null) => {
        // Warn if no token is provided
        if (!authToken) {
            console.warn('[ReadingAssistance] No authentication token provided. WebSocket may fail.');
        }

        let url = READING_ASSISTANCE_WS_URL;
        if (authToken) {
            const separator = url.includes('?') ? '&' : '?';
            url = `${url}${separator}token=${authToken}`;
        }
        socket.value = new WebSocket(url);
    };

    const setupSocketHandlers = (onMessage, onError, onClose) => {
        if (!socket.value) return;

        socket.value.onmessage = (event) => {
            const data = JSON.parse(event.data);
            if (onMessage) onMessage(data);
        };

        socket.value.onerror = (error) => {
            if (onError) onError(error);
        };

        socket.value.onclose = () => {
            if (onClose) onClose();
        };
    };

    const sendMessage = (payload) => {
        if (socket.value && socket.value.readyState === WebSocket.OPEN) {
            socket.value.send(JSON.stringify(payload));
            return true;
        }
        return false;
    };

    const sendSummarizeRequest = (resourceId, isSample) => {
        return sendMessage({
            request_type: WS_REQUEST_SUMMARIZE,
            resource_id: resourceId,
            is_sample: isSample,
        });
    };

    const sendGenerateQuestionsRequest = (resourceId, isSample, languageLevel, phase) => {
        return sendMessage({
            request_type: WS_REQUEST_GENERATE_QUESTIONS,
            resource_id: resourceId,
            is_sample: isSample,
            language_level: languageLevel,
            phase: phase,
        });
    };

    const waitForSocketReady = () => {
        return new Promise((resolve) => {
            if (socket.value && socket.value.readyState === WebSocket.OPEN) {
                resolve();
                return;
            }

            const maxAttempts = 50;
            let attempts = 0;

            const checkSocket = setInterval(() => {
                attempts++;
                if (socket.value && socket.value.readyState === WebSocket.OPEN) {
                    clearInterval(checkSocket);
                    resolve();
                } else if (attempts >= maxAttempts) {
                    clearInterval(checkSocket);
                    resolve();
                }
            }, 100);
        });
    };

    const closeSocket = () => {
        if (socket.value) {
            socket.value.close();
            socket.value = null;
        }
    };

    return {
        socket,
        initializeSocket,
        setupSocketHandlers,
        sendSummarizeRequest,
        sendGenerateQuestionsRequest,
        waitForSocketReady,
        closeSocket,
        WS_MESSAGE_SUMMARY_TOKEN,
        WS_MESSAGE_SUMMARY_DONE,
        WS_MESSAGE_QUESTION,
        WS_MESSAGE_QUESTIONS_DONE,
        WS_MESSAGE_ERROR,
    };
}
